import React, { useCallback, useMemo } from 'react';
import { useParams } from 'wouter';
import { useObserver } from '../../CodesThatsGottaGoSomewhereElse';
import { CommandDispatcher } from '../../concepts/commands/CommandDispatcher';
import { Icon } from '../../concepts/resources/Icon';
import { Colors } from '../../ui/Colors';
import { UseLocationScope } from '../AppLocation';
import { useResolve } from '../Container';
import { AccountHeaderBlock } from '../account/AccountHeaderBlock';
import { AccountPage } from '../account/AccountPage';
import { PageLoader } from '../account/PageLoader';
import { PlanStateHeaderBlock } from '../account/PlanStateHeaderBlock';
import { Crumbs } from '../common/Crumbs';
import { useFeatureCheck } from '../common/FeatureElements';
import { Page } from '../common/Page';
import { usePermissionCheck } from '../common/PermissionElements';
import { useReturnLocation } from '../common/ReturnLocation';
import { XmasLightsDashboard } from '../occasions/xmas/XmasLightsDashboard';
import { useUpgradePageOpener } from '../upgrade/UpgradeButton';
import { FolderBoardView } from '../workspace/FolderBoardView';
import { folderCreate } from '../workspace/FolderCommands';
import { QuickMenuStrip } from '../workspace/QuickMenuStrip';
import { WorkspacePageContent } from '../workspace/WorkspacePageContent';
import { templateCreate } from './TemplateCommands';
import { TemplateLibraryModel } from './TemplateLibraryModels';
import { TemplateLibraryScope } from './TemplateLibraryScope';

const createTemplateLibraryMenuStrip = (group: string) =>
    () => {

        const dispatch = useResolve(CommandDispatcher);
        const canEdit = usePermissionCheck('da:write', 'wo:write');
        const { workspaceId } = useObserver(useResolve(TemplateLibraryModel));

        const onAddFolder = useCallback(() => void (dispatch(folderCreate, [workspaceId])), [workspaceId]);
        const onAddTemplate = useCallback(() => void (dispatch(templateCreate, [group])), [group]);

        const onUpgrade = useUpgradePageOpener();
        const hasFeature = useFeatureCheck('workspaces');

        return <QuickMenuStrip>
            {!group ? <>
                <QuickMenuStrip.Button
                    label="New Folder"
                    icon="deqo://icons/dash/add-folder"
                    color={Colors.positive}
                    disabled={!canEdit || !hasFeature}
                    onClick={onAddFolder}
                />
            </> : <>
                <QuickMenuStrip.Button
                    label="Add Template"
                    icon="deqo://icons/dash/add-template"
                    color={Colors.mystery}
                    disabled={!canEdit || !hasFeature}
                    onClick={onAddTemplate}
                />
            </>}
            {!hasFeature && <QuickMenuStrip.Banner width="20" onClick={onUpgrade}>
                <Icon src="deqo://icons/i8/diamond" size={5} />
                Want to add custom templates?  Upgrade your space today!
            </QuickMenuStrip.Banner>}
        </QuickMenuStrip>;
    };

export const TemplateLibraryView = ({ group }: { group?: string }) => {

    const { folders } = useObserver(useResolve(TemplateLibraryModel));
    const selected = useObserver(folders.find(x => x.id === group));
    const ConfiguredMenuStrip = useMemo(() => createTemplateLibraryMenuStrip(group), [group]);

    const boardActions = {
        pin: false,
        move: false,
        clone: false,
    };

    return <>
        <UseLocationScope name="library" value="library" label="Template Library" />
        {selected && <UseLocationScope name="folder" value={selected.id} label={selected.label} />}
        <Page.Header>
            <Crumbs items={[
                ['Template Library', '/library'],
                !!selected ? [selected.label, null] : null,
            ]} />
            <Page.HeaderPush>
                <PlanStateHeaderBlock />
                <AccountHeaderBlock />
            </Page.HeaderPush>
        </Page.Header>
        <WorkspacePageContent top={ConfiguredMenuStrip}>
            <XmasLightsDashboard />
            {!selected ? (
                <FolderBoardView {...{ actions: false, boards: [], folders }} />
            ) : (
                <FolderBoardView {...{ actions: boardActions, boards: selected.boards, folders: [], }} />
            )}
        </WorkspacePageContent>
    </>;
};

export const TemplateLibraryPage = () => {
    const { group } = useParams<{ group: string }>();
    useReturnLocation(`/library/${group ?? ''}`);
    return <AccountPage>
        <TemplateLibraryScope loader={PageLoader}>
            <TemplateLibraryView group={group} />
        </TemplateLibraryScope>
    </AccountPage>;
};
